import { template } from "@ember/template-compiler";
import type Store from '@ember-data/store';
import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { type EmberChangeset } from 'ember-changeset';
import { t } from 'ember-intl';
import Medication from 'parog-web/models/prescriptions/medication';
import MedicationRequest from 'parog-web/models/prescriptions/medication-request';
import type TreatmentPrescriptionChangedJournalEvent from 'parog-web/models/treatment-prescription-changed-journal-event';
const ATC_TO_PRESCRIPTION = {
    B03X: {
        label: 'Traitement anti-anémique (hors fer, vitamine B12, folates)',
        cisCode: 'Traitement_anti_anemique'
    }
};
export interface Signature {
    Args: {
        changeset: EmberChangeset;
        codes: string[];
        treatmentPrescriptionChangedJournalEvents: TreatmentPrescriptionChangedJournalEvent[];
    };
}
export default class TreatmentsSection extends Component<Signature> {
    @service
    store: Store;
    @action
    addTreatment(code1: string, event1: PointerEvent) {
        event1.preventDefault();
        const treatments1 = this.args.changeset.get('prescriptions') ? [
            ...this.args.changeset.get('prescriptions')
        ] : [];
        const prescriptionDetails1 = ATC_TO_PRESCRIPTION[code1];
        const medicationPayload1 = {
            cisCode: prescriptionDetails1.cisCode,
            brand: prescriptionDetails1.label,
            longDescription: prescriptionDetails1.label,
            shortDescription: prescriptionDetails1.label,
            state: 'D',
            uid: prescriptionDetails1.label,
            molecule: {
                code: code1,
                label: prescriptionDetails1.label
            }
        };
        const medication1 = new Medication(medicationPayload1);
        treatments1.push(new MedicationRequest({
            medication: medication1
        }));
        this.args.changeset.set('prescriptions', treatments1);
    }
    @action
    ignoreTreatment(code1: string, event1: PointerEvent) {
        event1.preventDefault();
        const ignoredAtcCodes1 = this.args.changeset.get('ignoredAtcCodes') ? [
            ...this.args.changeset.get('ignoredAtcCodes')
        ] : [];
        ignoredAtcCodes1.push(code1);
        this.args.changeset.set('ignoredAtcCodes', ignoredAtcCodes1);
    }
    alreadyHadTreatment(code1: string, treatmentPrescriptionChangedJournalEvents1: TreatmentPrescriptionChangedJournalEvent[]): boolean {
        return treatmentPrescriptionChangedJournalEvents1?.some((treatmentPrescriptionChangedJournalEvent1)=>treatmentPrescriptionChangedJournalEvent1.atcCodes.includes(code1));
    }
    get codes(): string[] {
        const existingTreatments1 = this.args.changeset.get('prescriptions');
        const existingIgnoredTreatments1 = this.args.changeset.get('ignoredAtcCodes');
        /**
     * Filter the suggestions if some have already been ignored
     * or added to the treatments list
     */ return this.args.codes.reduce((acc1: string[], curr1)=>{
            if (existingTreatments1?.some((prescription1)=>prescription1?.medication?.molecule?.code === curr1) || existingIgnoredTreatments1.includes(curr1) || this.alreadyHadTreatment(curr1, this.args.treatmentPrescriptionChangedJournalEvents)) {
                return acc1;
            }
            acc1.push(curr1);
            return acc1;
        }, []);
    }
    static{
        template(`
    {{#if this.codes.length}}
      <div class='ui message'>
        <span>Votre patient prend-il un des traitements ci-après ?</span>
        <ul>
          {{#each this.codes as |code|}}
            <li class='[&:not(:first-child)>div:first]:pt-2'>
              <div
                class='grid grid-cols-[1.5fr_0.5fr_0.5fr_1fr] [.ui.message_li:not(:first-child)_&]:pt-[0.5em]'
              >
                <span class='mr16'>{{t (concat 'atc.' code)}}</span>
                <button
                  class='ui mini primary button mr16 px-4 py-2'
                  type='button'
                  data-test-add-button={{code}}
                  {{on 'click' (fn this.addTreatment code)}}
                >
                  Oui
                </button>
                <button
                  class='ui mini primary button mr16 px-4 py-2'
                  type='button'
                  data-test-ignore-button={{code}}
                  {{on 'click' (fn this.ignoreTreatment code)}}
                >
                  Non
                </button>
              </div>
            </li>
          {{/each}}
        </ul>
      </div>
    {{/if}}
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
